// Anonymous analytics client. Generates a per-browser UUID stored in
// localStorage so we can measure DAU / MAU / retention without ever
// collecting PII. Events are fire-and-forget — analytics MUST never
// block or fail kid UX.
//
// This module has TWO sinks:
//   1) Our own FastAPI endpoint (/api/event) — anonymous, PII-free.
//   2) Google Analytics 4 via gtag (loaded from public/index.html).
//      Every trackEvent() also forwards to gtag('event', name, props)
//      so kid-facing conversions (stamps, quizzes, games, missions)
//      show up in GA4 real-time + funnels with zero per-callsite
//      changes.
//
// Cycle 58 (Feb 2026) additions:
//   • Standard funnel params attached to landed / first_tap_* /
//     started_activity / completed_activity — country (GA4 auto),
//     device_category, is_low_end, language, is_first_session.
//   • Typed helpers `trackStartedActivity` / `trackCompletedActivity`
//     that guarantee once-per-session firing for `started_activity`
//     and consistent shape (activity_id, strand, source,
//     is_first_session).
//   • `isDebugMode()` + `debugWarn()` for local dev QA (query param
//     ?ga_debug=1 or localStorage mwq_ga_debug=1).
//
// Usage:
//   import { trackEvent, trackStartedActivity } from "@/lib/analytics";
//   trackEvent("quiz_complete", { sectionId: "history", score: 8, total: 10 });
//   trackStartedActivity({ activity_id: "lesson/uk-ks2-en-2", strand: "reading-writing" });

const ANON_KEY  = "we_anon_id_v1";
const FIRST_KEY = "we_first_session_v1";
const BASE = process.env.REACT_APP_BACKEND_URL || "";

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getAnonId() {
  try {
    let v = localStorage.getItem(ANON_KEY);
    if (!v) {
      v = uuid();
      localStorage.setItem(ANON_KEY, v);
      // If we've never seen this browser, this is the first session
      // (won't survive a hard reset — matches GA4's own semantics).
      try { localStorage.setItem(FIRST_KEY, "1"); } catch { /* ignore */ }
    }
    return v;
  } catch { return "no-storage"; }
}

/** true if this browser has never seen a completed session before. */
export function isFirstSession() {
  try {
    // Once trackEvent has fired `session_end` (or similar), we clear
    // FIRST_KEY. For now we treat "no anon id yet OR the flag was set
    // when the anon id was created" as first session.
    return localStorage.getItem(FIRST_KEY) === "1";
  } catch { return false; }
}

/** Called once when the first meaningful activity finishes so future
 *  sessions are correctly tagged is_first_session=false. */
export function clearFirstSessionFlag() {
  try { localStorage.setItem(FIRST_KEY, "0"); } catch { /* ignore */ }
}

// ─── Debug mode ────────────────────────────────────────────────────
export function isDebugMode() {
  try {
    if (typeof window === "undefined") return false;
    if (window.location && window.location.search.includes("ga_debug=1")) return true;
    return localStorage.getItem("mwq_ga_debug") === "1";
  } catch { return false; }
}
export function debugWarn(...args) {
  if (isDebugMode()) {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
}

// ─── Standard funnel params ────────────────────────────────────────
function _deviceCategory() {
  try {
    if (typeof navigator === "undefined") return "unknown";
    const ua = navigator.userAgent || "";
    if (/Mobi|Android/i.test(ua)) return "mobile";
    if (/iPad|Tablet/i.test(ua)) return "tablet";
    return "desktop";
  } catch { return "unknown"; }
}
function _isLowEnd() {
  try {
    if (typeof navigator === "undefined") return false;
    // "hardwareConcurrency" is a rough proxy — <=2 = likely low-end.
    const cores = navigator.hardwareConcurrency || 0;
    return cores > 0 && cores <= 2;
  } catch { return false; }
}
function _language() {
  try {
    if (typeof document !== "undefined") {
      const html = document.documentElement.getAttribute("lang");
      if (html) return html;
    }
    if (typeof navigator !== "undefined") return navigator.language || "en";
    return "en";
  } catch { return "en"; }
}

/** Events that carry funnel standard params. */
const FUNNEL_EVENTS = new Set([
  "landed",
  "first_tap_10s",
  "first_tap_30s",
  "left_30s_no_interaction",
  "stayed_30s_no_tap",
  "started_activity",
  "completed_activity",
]);

function _augment(name, props) {
  if (!FUNNEL_EVENTS.has(name)) return props || {};
  return {
    ...(props || {}),
    device_category: _deviceCategory(),
    is_low_end: _isLowEnd(),
    language: _language(),
    is_first_session: isFirstSession(),
  };
}

// ─── GA4 wire ──────────────────────────────────────────────────────
function toGAEventName(name) {
  return String(name || "event").replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 40);
}
function toGAParams(props) {
  const out = {};
  if (!props || typeof props !== "object") return out;
  for (const [k, v] of Object.entries(props)) {
    const key = String(k).replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 40);
    if (v == null) continue;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      out[key] = typeof v === "string" ? v.slice(0, 100) : v;
    } else {
      try { out[key] = JSON.stringify(v).slice(0, 100); } catch { /* skip */ }
    }
  }
  return out;
}
function sendToGA(name, props) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", toGAEventName(name), toGAParams(props));
    }
  } catch { /* GA errors must NEVER break UX */ }
}

// ─── Public API ────────────────────────────────────────────────────
function _sharedSession() {
  try { return window.__MWQ_PACKAGE__ === true || /^\/airline(\/|$)/.test(window.location.pathname) || sessionStorage.getItem("mwq_airline_session") === "1" || localStorage.getItem("mwq_runtime_profile") === "AIRLINE_EVALUATION"; } catch { return false; }
}

export function trackEvent(name, props = {}) {
  if (_sharedSession()) return; // anonymous shared sessions: no telemetry leaves the device
  const enriched = _augment(name, props);
  if (isDebugMode()) debugWarn(`[analytics] ${name}`, enriched);
  // Cycle 69 · append to the device-local ring so ExperienceQuality
  // dashboard can compute rates without a fleet backend. Best-effort.
  try {
    let tier = "unknown";
    try {
      const g = localStorage.getItem("we_age_group");
      if (g === "little") tier = "seedlings";
      else if (g === "big") tier = "explorers";
      else if (g === "older") tier = "pioneers";
    } catch { /* ignore */ }
    const ringArr = JSON.parse(localStorage.getItem("we_events_ring_v1") || "[]");
    ringArr.push({ name, at: new Date().toISOString(), tier, props: enriched });
    localStorage.setItem("we_events_ring_v1", JSON.stringify(ringArr.slice(-500)));
  } catch { /* ignore */ }
  sendToGA(name, enriched);
  if (!BASE) return;
  const body = JSON.stringify({ anon_id: getAnonId(), name, props: enriched });
  try {
    if (navigator?.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(`${BASE}/api/event`, blob);
      if (ok) return;
    }
  } catch {/* fall through */}
  try {
    fetch(`${BASE}/api/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {/* ignore */}
}

export function trackPageview(path, title) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path || (typeof window !== "undefined" ? window.location.pathname : "/"),
        page_title: title || (typeof document !== "undefined" ? document.title : ""),
        page_location: typeof window !== "undefined" ? window.location.href : undefined,
      });
    }
  } catch { /* ignore */ }
}

// ─── Activation funnel helpers ─────────────────────────────────────
// `started_activity` fires ONCE per session (guarded by
// sessionStorage) — the moment a child begins a real learning
// activity. This is the GA4 KEY EVENT that defines activation.
const SESSION_STARTED_ACT_KEY = "we_started_activity_v1";

export function trackStartedActivity({ activity_id, strand = "unknown", source = "unknown", attempt_count = 1 } = {}) {
  try {
    if (sessionStorage.getItem(SESSION_STARTED_ACT_KEY) === "1") {
      // Already fired for this session — do NOT double-count. Still
      // fire a "reactivated" event that isn't a key event so we know
      // the child came back to another activity.
      trackEvent("reactivated_activity", { activity_id, strand, source });
      return;
    }
    sessionStorage.setItem(SESSION_STARTED_ACT_KEY, "1");
  } catch { /* ignore */ }
  trackEvent("started_activity", {
    activity_id: String(activity_id || "unknown").slice(0, 100),
    strand: String(strand || "unknown"),
    source: String(source || "unknown"),
    attempt_count: Number(attempt_count) || 1,
  });
}

/** Fires on every activity completion (not deduped — a child may
 *  finish multiple activities per session; each is a real signal). */
export function trackCompletedActivity({ activity_id, strand = "unknown", result = "attempted", attempt_count = 1 } = {}) {
  trackEvent("completed_activity", {
    activity_id: String(activity_id || "unknown").slice(0, 100),
    strand: String(strand || "unknown"),
    result: String(result || "attempted"), // mastered | attempted | struggled
    attempt_count: Number(attempt_count) || 1,
  });
  // North-Star canonical event: first-ever completion on this learner (same anon_id as first_activity_started).
  try {
    if (!localStorage.getItem("we_first_activity_completed_v1")) {
      localStorage.setItem("we_first_activity_completed_v1", new Date().toISOString());
      trackEvent("first_activity_completed", { activity_id: String(activity_id || "unknown").slice(0, 100), result: String(result || "attempted") });
    }
  } catch { /* ignore */ }
  // After first successful completion this browser is no longer a
  // brand-new install for the funnel's `is_first_session` flag.
  if (result === "mastered") clearFirstSessionFlag();
}

// ─── Engagement heartbeat + session quality (Feb 2026 · Round 3) ────
// Feb 2026 GA4 audit showed avg engagement of ~1:17 with only ~54% of
// users firing user_engagement. Root cause: canvas/game/globe views
// don't fire page_view or engagement timers, and SPA route changes
// don't re-fire them either. This block adds:
//   · A single window-level heartbeat that ticks every 15s of ACTIVE
//     interaction (paused on blur/idle/hidden tab).
//   · activity_engaged_60s: fired once when a child crosses 60s of
//     genuinely active time in the current activity.
//   · session_quality: fired at session end with aggregate counters.
//   · returning_visit: fired on app open when prior local progress
//     exists, with days_since_last_visit + visit_number + PWA flag.
const HB_INTERVAL_MS = 15_000;
const IDLE_MS = 45_000;
const LAST_VISIT_KEY = "mwq_last_visit_iso";
const VISIT_COUNT_KEY = "mwq_visit_count";
const SESSION_QUALITY_KEY = "mwq_session_quality_v1";

let _hbTimer = null;
let _hbLastActive = 0;
let _hbActivitySeconds = 0;         // total active seconds this session
let _hbActivityCurrent = null;      // { id, seconds } for current activity
let _hb60Fired = new Set();         // activity_ids that already fired 60s
let _hbInstalled = false;

function _isVisible() {
  if (typeof document === "undefined") return true;
  return document.visibilityState !== "hidden";
}

function _isPwaInstalled() {
  try {
    if (typeof window === "undefined") return false;
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    if (window.navigator?.standalone === true) return true;
  } catch { /* ignore */ }
  return false;
}

/** Note that the user just interacted (click/scroll/keypress/touch).
 *  Called by the top-level EngagementTracker component. */
export function noteActive() {
  _hbLastActive = Date.now();
}

/** Start tracking a specific activity (e.g. "game:dodge", "quiz:xyz",
 *  "coloring:foxy"). Persists 60s+ engagement counters. */
export function beginActivity(activityId) {
  if (!activityId) return;
  _hbActivityCurrent = { id: String(activityId).slice(0, 80), seconds: 0 };
  noteActive();
}

export function endActivity() {
  _hbActivityCurrent = null;
}

function _tick() {
  if (!_isVisible()) return;
  const now = Date.now();
  if (_hbLastActive === 0) return;
  if (now - _hbLastActive > IDLE_MS) return;
  // Genuine active interval — count 15s toward totals.
  _hbActivitySeconds += HB_INTERVAL_MS / 1000;
  if (_hbActivityCurrent) {
    _hbActivityCurrent.seconds += HB_INTERVAL_MS / 1000;
    if (_hbActivityCurrent.seconds >= 60 && !_hb60Fired.has(_hbActivityCurrent.id)) {
      _hb60Fired.add(_hbActivityCurrent.id);
      trackEvent("activity_engaged_60s", { activity_id: _hbActivityCurrent.id });
    }
  }
  // Heartbeat ping — GA4 uses these to compute engagement time.
  trackEvent("engagement_heartbeat", {
    active_seconds_total: Math.round(_hbActivitySeconds),
    activity_id: _hbActivityCurrent?.id || "none",
  });
}

/** Called once by the top-level EngagementTracker to install listeners
 *  and start the heartbeat. Safe to call multiple times — the second
 *  call is a no-op. */
export function installEngagementHeartbeat() {
  if (_hbInstalled || typeof window === "undefined") return;
  _hbInstalled = true;
  ["click", "keydown", "touchstart", "scroll", "pointermove"].forEach((evt) =>
    window.addEventListener(evt, noteActive, { passive: true, capture: true })
  );
  document.addEventListener("visibilitychange", () => {
    if (_isVisible()) noteActive();
  });
  _hbTimer = window.setInterval(_tick, HB_INTERVAL_MS);

  // Session quality — flush on pagehide (fires on close AND background
  // on mobile Safari, which `unload` does not).
  const flushQuality = () => {
    try {
      const q = JSON.parse(localStorage.getItem(SESSION_QUALITY_KEY) || "{}");
      trackEvent("session_quality", {
        activities_started: Number(q.activities_started) || 0,
        activities_completed: Number(q.activities_completed) || 0,
        total_active_seconds: Math.round(_hbActivitySeconds),
        games_played: Number(q.games_played) || 0,
        lessons_viewed: Number(q.lessons_viewed) || 0,
      });
    } catch { /* ignore */ }
  };
  window.addEventListener("pagehide", flushQuality);

  // Returning-visit event — fire once per app-open when prior state
  // exists. `days_since_last_visit` is null on the very first visit.
  try {
    const lastIso = localStorage.getItem(LAST_VISIT_KEY);
    const visitNum = (Number(localStorage.getItem(VISIT_COUNT_KEY)) || 0) + 1;
    localStorage.setItem(VISIT_COUNT_KEY, String(visitNum));
    const now = new Date();
    localStorage.setItem(LAST_VISIT_KEY, now.toISOString());
    let daysSince = null;
    if (lastIso) {
      const last = new Date(lastIso);
      daysSince = Math.floor((now - last) / 86_400_000);
    }
    if (visitNum > 1) {
      trackEvent("returning_visit", {
        days_since_last_visit: daysSince ?? -1,
        visit_number: visitNum,
        has_installed_pwa: _isPwaInstalled(),
      });
    }
  } catch { /* ignore */ }
}

/** Public helper for pages to bump their session-quality counters. */
export function bumpSessionCounter(key) {
  try {
    const q = JSON.parse(localStorage.getItem(SESSION_QUALITY_KEY) || "{}");
    q[key] = (Number(q[key]) || 0) + 1;
    localStorage.setItem(SESSION_QUALITY_KEY, JSON.stringify(q));
  } catch { /* ignore */ }
}

/** Cycle 66 · Homepage four-pathway launcher click. */
export function trackPathway(pathway) {
  trackEvent("home_pathway_click", { pathway: String(pathway || "unknown").slice(0, 40) });
}

// ─── Jun 2026 · Named product-measurement events ───────────────────
// Thin, privacy-respecting wrappers over trackEvent so the funnel the
// brief asks for is explicit and consistent. No child PII is ever sent.
export function trackQuestStarted(props = {})   { trackEvent("quest_started", props); }
export function trackQuestCompleted(props = {}) { trackEvent("quest_completed", props); }
export function trackQuestAbandoned(props = {}) { trackEvent("quest_abandoned", props); }
export function trackParentPageViewed(page = "parents")     { trackEvent("parent_page_viewed", { page: String(page).slice(0, 40) }); }
export function trackProgressOrReportViewed(kind = "report"){ trackEvent("progress_or_report_viewed", { kind: String(kind).slice(0, 40) }); }
export function trackResourceDownloaded(props = {}) { trackEvent("resource_downloaded", props); }
export function trackResourceSearched(props = {})   { trackEvent("resource_searched", props); }
export function trackResourceViewed(props = {})     { trackEvent("resource_viewed", props); }
export function trackSchoolPageViewed(page = "schools") { trackEvent("school_page_viewed", { page: String(page).slice(0, 40) }); }
export function trackEnquiryOrInformationAction(props = {}) { trackEvent("enquiry_or_information_action", props); }

// ─── Jun 2026 · Homepage / globe events (deduped per session) ───────
const _sectionViewedThisSession = new Set();
/** Fires once per section per session. */
export function trackHomeSectionViewed(section) {
  const key = String(section || "unknown").slice(0, 40);
  if (_sectionViewedThisSession.has(key)) return;
  _sectionViewedThisSession.add(key);
  trackEvent("home_section_viewed", { section: key });
}
export function trackGlobeMounted(mode = "3d")     { trackEvent("globe_mounted", { mode: String(mode).slice(0, 12) }); }
export function trackGlobeCountryTapped(code = "") { trackEvent("globe_country_tapped", { code: String(code).slice(0, 8) }); }
export function trackGlobeWonderTapped(id = "")    { trackEvent("globe_wonder_tapped", { id: String(id).slice(0, 40) }); }
export function trackGlobeSpinRandom()             { trackEvent("globe_spin_random_used", {}); }
export function trackHomeModuleOpened(module = "") { trackEvent("home_module_opened", { module: String(module).slice(0, 40) }); }
export function trackMathsStrandTapped(strand = ""){ trackEvent("maths_strand_tapped", { strand: String(strand).slice(0, 60) }); }
export function trackTreasureWorldTapped(world = ""){ trackEvent("treasure_world_tapped", { world: String(world).slice(0, 40) }); }

// Cycle 69 · Master Command — a small device-local event ring so the
// Experience Quality dashboard (`/admin/experience`) can compute
// stakeholder success rates without needing a whole-fleet analytics
// backend. All events sent via `trackEvent` are also appended here.
const EVENT_RING_KEY = "we_events_ring_v1";
const EVENT_RING_MAX = 500;

/** Read the current device's event ring (newest last). Best-effort. */
export function readEventRing() {
  try {
    const arr = JSON.parse(localStorage.getItem(EVENT_RING_KEY) || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

/** Append an event onto the ring. Called by `trackEvent`. */
export function _appendToRing(evt) {
  try {
    const arr = readEventRing();
    arr.push(evt);
    localStorage.setItem(EVENT_RING_KEY, JSON.stringify(arr.slice(-EVENT_RING_MAX)));
  } catch { /* ignore */ }
}
